import { Header } from './Header';
import { HttpStatusCode } from './HttpStatusCode';
import { getItem } from '../../services/session-storage.service';
import { HttpError } from './HttpError';
import { QueryParameter } from './QueryParameter';

export const Transformer = {
  getHeaders: (method, token, options) => {
    const returnedHeaders = new Headers(options?.headers);
    if (token) {
      returnedHeaders.set(Header.AUTHORIZATION, `Bearer ${token}`);
    }
    if (options?.useCache) {
      returnedHeaders.set(Header.CACHE.RESPONSE.NAME, Header.CACHE.RESPONSE.VALUE);
    }
    if (options?.useMSW) {
      returnedHeaders.set(Header.MSW.USE.NAME, Header.MSW.USE.VALUE);
    }
    if (['POST', 'PUT', 'PATCH'].includes(method)) returnedHeaders.set(Header.CONTENT_TYPE.NAME, Header.CONTENT_TYPE.VALUE.JSON);
    if (!returnedHeaders.has(Header.TRANSLATION.NAME)) {
      returnedHeaders.set(Header.TRANSLATION.NAME, Header.TRANSLATION.VALUE.DEFAULT);
    }
    return returnedHeaders;
  },
  setQueryParameters: (url, queryParameters) => {
    if (queryParameters && queryParameters.length > 0) {
      const targetUrl = new URL(url);
      const { searchParams } = targetUrl;
      queryParameters.forEach(element => {
        const { name, value, overwrite = true } = element;
        if (overwrite) {
          // Set the parameter, replacing existing ones with the same key
          searchParams.set(name, value);
        } else {
          // Append the parameter without replacing existing ones
          searchParams.append(name, value);
        }
      });
      const returnedUrl = `${targetUrl.protocol}//${targetUrl.host}${
        targetUrl.pathname === '/' ? '' : targetUrl.pathname
      }?${searchParams.toString()}`;
      return returnedUrl;
    }
    return url;
  },
  createRequestInfo: async (method, url, options) => {
    let token = '';
    try {
      token = getItem('token');
    } catch (ex) {
      // On error we do nothing, we just not add the Authorization header
    }
    const fullUrl = Transformer.setQueryParameters(url, options?.queryParameters);
    const requestInit = {
      method,
      headers: Transformer.getHeaders(method, token, options),
      body: options?.body ? JSON.stringify(options?.body) : undefined,
//      credentials: 'include' as RequestCredentials,
      signal: options?.signal
    };
    return {
      fullUrl,
      init: requestInit,
      preventRetryOnStatus: options?.preventRetryOnStatus,
      maxRetries: options?.maxRetries
    };
  },
  checkErrorStatus: async (response, requestInfo) => {
    if (response.status < HttpStatusCode.OK || response.status > 299) {
      console.error(new Error(response.statusText), {
        type: 'apiError',
        level: 'high',
        httpStatus: response.status,
        apiUrl: requestInfo.fullUrl,
        xCorrelationId: response.headers.get(Header.CORRELATION)
      });
      const errorBody = await Transformer.getBody(response);
      const error = { status: response.status, message: response.statusText, body: errorBody, headers: response.headers } as HttpError;
      throw error;
    }
  },
  wait: delay => {
    return new Promise(resolve => {
      setTimeout(resolve, delay);
    });
  },
  fetchPaginated: async (url, options) => {
    const request = await Transformer.createRequestInfo('GET', url, options);
    const response = await Transformer.fetch(request);
    const { totalPages } = Transformer.getPaginationHeaders(response.headers);
    if (totalPages && totalPages > 1) {
      const queryParameters = options?.queryParameters ?? [];
      const promises = [];
      for (let page = 2; page <= totalPages; page += 1) {
        const fullUrl = Transformer.setQueryParameters(url, [
          ...queryParameters,
          {
            name: QueryParameter.PAGINATION.PAGE,
            value: page.toString()
          }
        ]);
        const followingRequest = { ...request, fullUrl };
        promises.push(Transformer.fetch(followingRequest));
      }
      const responses = await Promise.all(promises);
      const results = responses.map(nextResponse => nextResponse.body);
      const flattenResults = results.reduce((previousResult, currentResult) => previousResult.concat(currentResult), []);
      response.body.push(...flattenResults);
    }
    // Pagination object is removed because it doesn't make sense to keep it here
    return {
      ...response,
      pagination: undefined
    };
  },
  fetch: async requestInfo => {
    const response = await fetch(requestInfo.fullUrl, requestInfo.init);
    //response = await Transformer.handleRetryAfter(requestInfo, response);
    const transformedResponse = await Transformer.transform(requestInfo, response);

    return transformedResponse;
  },
  transform: async (requestInfo, response) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const body = await Transformer.getBody(response);
    const transformedResponse = {
      status: Number(response.status),
      statusText: response.statusText,
      body,
      headers: response.headers /*,
      pagination: Transformer.getPagination(requestInfo.fullUrl, response.headers)*/
    };
    return transformedResponse;
  },
  getBody: async response => {
    const contentType = response.headers.get(Header.CONTENT_TYPE.NAME);
    if (response.status === HttpStatusCode.NO_CONTENT || !contentType)
      // If the response has no content, no need to read an empty stream
      return undefined;
    if (contentType.indexOf(Header.CONTENT_TYPE.VALUE.JSON) > -1) return response.json();
    if (Header.CONTENT_TYPE.VALUE.FILES.includes(contentType)) return response.blob();
    return response.text();
  },
  getPaginationHeaders: headers => {
    const totalPages = headers.get(Header.PAGINATION.TOTAL_PAGES);
    const currentPage = headers.get(Header.PAGINATION.CURRENT_PAGE);
    return {
      currentPage: currentPage ? +currentPage : undefined,
      totalPages: totalPages ? +totalPages : undefined
    };
  },
  getPagination: (url, headers) => {
    const { totalPages, currentPage } = Transformer.getPaginationHeaders(headers);
    if (!currentPage || !totalPages) {
      return undefined;
    }
    let previousPage;
    let nextPage;
    if (currentPage > 1) {
      previousPage = Transformer.setQueryParameters(url, [
        {
          name: QueryParameter.PAGINATION.PAGE,
          value: (currentPage - 1).toString()
        }
      ]);
    }
    if (currentPage < totalPages) {
      nextPage = Transformer.setQueryParameters(url, [
        {
          name: QueryParameter.PAGINATION.PAGE,
          value: (currentPage + 1).toString()
        }
      ]);
    }
    return { previousPage, nextPage };
  }
};
